import { FC, useState } from 'react';
import { Backdrop, Button, CircularProgress, Modal } from '@mui/material';
import SimpleInput from 'src/components/Items/input/SimpleInput';
import SecondTypeButton from 'src/components/Items/button/SecondTypeButton';
import PasswordInput from 'src/components/Items/input/PasswordInput';
import { useActions } from 'src/hooks/useActions';
import { useTypedSelector } from 'src/hooks/useTypeSelector';
import { validateEmail, validateField } from 'src/common/utils/validation';
import './styles.scss';

interface IProps {
  open: boolean;
  onClose: () => void;
}

const LoginModal: FC<IProps> = ({ open, onClose }) => {
  const { login, signup } = useActions();
  const { isLoading, result } = useTypedSelector((state) => state.client);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [isSignup, setIsSignup] = useState(false);
  const [check, setCheck] = useState(false);
  const [errors, setErrors] = useState({ email: '', password: '' });

  const handleInput = (name: string) => (value: string) => {
    setLoginData({ ...loginData, [name]: value });
  };

  const validateLogin = (): boolean => {
    const email = validateEmail(loginData.email);
    const password = validateField(loginData.password);
    setErrors({ email, password });
    return [email, password].every((el) => !el);
  };

  const validateSignUp = (): boolean => {
    const email = validateEmail(loginData.email);
    setErrors({ ...errors, email });
    return [email].every((el) => !el);
  };

  const handleLogin = () => {
    setCheck(true);
    if (validateLogin()) {
      login(loginData);
    }
    setCheck(false);
  };

  const handleSignUp = () => {
    setCheck(true);
    if (validateSignUp()) {
      signup(loginData.email);
      setErrors({ email: '', password: '' });
    }
    setCheck(false);
  };

  const handleClose = () => {
    setLoginData({ email: '', password: '' });
    setErrors({ email: '', password: '' });
    setIsSignup(false);
    setCheck(false);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      BackdropComponent={Backdrop}
      closeAfterTransition
    >
      <>
        <div className='login-modal'>
          {isSignup ? (
            <>
              <h3 className='login-modal__title'>Sign Up</h3>
              {isLoading.signup ? (
                <CircularProgress size={100} />
              ) : (
                <>
                  {result.signup ? (
                    <>
                      <h4 className='login-modal__signup__text'>
                        Sent the password to the phone number that was specified when making the booking
                      </h4>
                      <PasswordInput
                        data={loginData.password}
                        setData={handleInput('password')}
                        title='Password'
                        placeholder='Enter password'
                        error={errors.password}
                        check={check}
                        style={{ marginBottom: '20px', textAlign: 'left' }}
                      />
                      <div className='login-modal__buttons'>
                        <SecondTypeButton
                          children='Cancel'
                          onClick={handleClose}
                        />
                        <Button
                          variant='contained'
                          onClick={handleLogin}
                          children='Log In'
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <SimpleInput
                        data={loginData.email}
                        setData={handleInput('email')}
                        title='Email'
                        placeholder='Enter your email'
                        style={{ marginBottom: '20px', textAlign: 'left' }}
                        check={check}
                        error={errors.email}
                      />
                      <div className='login-modal__signup'>
                        Already have an account?
                        <b onClick={() => setIsSignup(false)}>Log in here</b>
                      </div>
                      <div className='login-modal__buttons'>
                        <SecondTypeButton
                          children='Cancel'
                          onClick={handleClose}
                        />
                        <Button
                          variant='contained'
                          onClick={handleSignUp}
                          children='Get password'
                        />
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <h3 className='login-modal__title'>Log In</h3>
              {isLoading.login ? (
                <CircularProgress size={100} />
              ) : (
                <>
                  <SimpleInput
                    data={loginData.email}
                    setData={handleInput('email')}
                    title='Email'
                    placeholder='Enter your email'
                    style={{ marginBottom: '20px', textAlign: 'left' }}
                    check={check}
                    error={errors.email}
                  />
                  <PasswordInput
                    data={loginData.password}
                    setData={handleInput('password')}
                    title='Password'
                    placeholder='Enter password'
                    error={errors.password}
                    check={check}
                    style={{ marginBottom: '20px', textAlign: 'left' }}
                  />
                  <div className='login-modal__signup'>
                    Don't have an account?
                    <b onClick={() => setIsSignup(true)}>Sign up here</b>
                  </div>
                  <div className='login-modal__buttons'>
                    <SecondTypeButton
                      children='Cancel'
                      onClick={handleClose}
                    />
                    <Button
                      variant='contained'
                      onClick={handleLogin}
                      children='Log In'
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </>
    </Modal >
  );
};
export default LoginModal;
